<template>
  <div class="client-master-list">
    <div class="client-master-list__header">
      <h3 class="client-master-list__title">Clients</h3>
      <div class="client-master-list__search-wrap">
        <MagnifyingGlassIcon class="client-master-list__search-icon" />
        <input
          v-model="searchQuery"
          type="search"
          class="client-master-list__search"
          placeholder="Rechercher un client..."
          aria-label="Rechercher"
        />
      </div>
    </div>

    <div v-if="loading" class="client-master-list__body">
      <div v-for="i in 6" :key="'sk-' + i" class="client-master-list__item client-master-list__item--skeleton">
        <SkeletonLoader variant="card" />
      </div>
    </div>

    <div v-else-if="errorMessage" class="client-master-list__error">
      <ExclamationCircleIcon class="client-master-list__error-icon" />
      <p>{{ errorMessage }}</p>
    </div>

    <div v-else-if="filteredClients.length === 0" class="client-master-list__empty">
      <UserGroupIcon class="client-master-list__empty-icon" />
      <p>Aucun client trouvé</p>
    </div>

    <div v-else class="client-master-list__body">
      <button
        v-for="client in filteredClients"
        :key="client.login || client.username || client.email"
        type="button"
        class="client-master-list__item"
        :class="{ 'client-master-list__item--active': selectedId === (client.login || client.username) }"
        @click="selectClient(client)"
      >
        <div class="client-master-list__item-avatar">
          {{ getInitials(client) }}
        </div>
        <div class="client-master-list__item-content">
          <span class="client-master-list__item-code">{{ client.login || client.username || '—' }}</span>
          <span class="client-master-list__item-email">{{ client.email || '—' }}</span>
        </div>
        <ChevronRightIcon
          v-if="selectedId === (client.login || client.username)"
          class="client-master-list__item-chevron"
        />
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { MagnifyingGlassIcon, UserGroupIcon, ExclamationCircleIcon, ChevronRightIcon } from '@heroicons/vue/24/outline';
import { isAuthenticatedSession } from '@/utils/sessionAuth';
import { fetchStandardClients } from '@/api/clients';
import SkeletonLoader from './SkeletonLoader.vue';


const props = defineProps({
  selectedId: { type: [Number, String], default: null },
  anonymize: { type: Boolean, default: false },
});

const emit = defineEmits(['select']);

const clients = ref([]);
const searchQuery = ref('');
const loading = ref(true);
const errorMessage = ref('');

function clientSortTimestamp(client) {
  const raw = client?.date_creation;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** Plus récent → plus ancien (date d’inscription client). */
function sortClientsNewestFirst(list) {
  return [...list].sort((a, b) => clientSortTimestamp(b) - clientSortTimestamp(a));
}

const filteredClients = computed(() => {
  const q = (searchQuery.value || '').trim().toLowerCase();
  if (!q) return clients.value;
  return clients.value.filter(
    (c) =>
      (c.login || '').toLowerCase().includes(q) ||
      (c.username || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (!props.anonymize && (c.nom || '').toLowerCase().includes(q)) ||
      (!props.anonymize && (c.prenom || '').toLowerCase().includes(q))
  );
});

function getInitials(client) {
  if (!props.anonymize) {
    const p = (client.prenom || '').trim()[0] || '';
    const n = (client.nom || '').trim()[0] || '';
    if (p || n) return (p + n).toUpperCase();
  }
  const login = (client.login || client.username || '').trim();
  return login.slice(0, 2).toUpperCase() || '?';
}

function selectClient(client) {
  emit('select', client);
}

async function fetchClients() {
  loading.value = true;
  errorMessage.value = '';
  try {
    if (!isAuthenticatedSession()) {
      errorMessage.value = 'Session expirée. Veuillez vous reconnecter.';
      return;
    }
    const raw = await fetchStandardClients();
    const mapped = raw.map((c) => ({
      nom: props.anonymize ? '' : c.nom || '',
      prenom: props.anonymize ? '' : c.prenom || '',
      login: c.login || c.username || '',
      username: c.username || c.login || '',
      email: c.email || '',
      tel: c.tel || '',
      date_creation: c.date_creation || null,
    }));
    clients.value = sortClientsNewestFirst(mapped);
  } catch (err) {
    if (err.response?.status === 401) errorMessage.value = 'Session expirée.';
    else errorMessage.value = err.response?.data?.detail || 'Impossible de charger les clients.';
    clients.value = [];
  } finally {
    loading.value = false;
  }
}

onMounted(() => fetchClients());

defineExpose({ refresh: fetchClients });
</script>

<style scoped>
.client-master-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  font-family: var(--font-primary);
  color: rgba(10, 26, 47, 0.92);
  background: rgba(255, 255, 255, 0.92);
  border-radius: 1rem;
  border: 1px solid rgba(10, 26, 47, 0.12);
  overflow: hidden;
  box-shadow: 0 18px 44px rgba(10, 26, 47, 0.06);
}

.client-master-list__header {
  padding: 1rem;
  border-bottom: 1px solid rgba(10, 26, 47, 0.08);
  flex-shrink: 0;
}

.client-master-list__title {
  font-size: var(--text-base);
  font-weight: var(--font-weight-semibold);
  color: rgba(10, 26, 47, 0.9);
  margin: 0 0 0.75rem 0;
}

.client-master-list__search-wrap {
  position: relative;
}

.client-master-list__search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  color: var(--bank-gray-400);
  pointer-events: none;
}

.client-master-list__search {
  width: 100%;
  box-sizing: border-box;
  padding: 0.65rem 1rem 0.65rem 2.5rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(10, 26, 47, 0.14);
  background: rgba(255, 255, 255, 0.85);
  color: rgba(10, 26, 47, 0.92);
  font-size: var(--text-sm);
  font-family: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.client-master-list__search::placeholder {
  color: var(--bank-gray-400);
}

.client-master-list__search:focus {
  outline: none;
  border-color: rgba(212, 175, 55, 0.65);
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 0 0 0.2rem rgba(212, 175, 55, 0.18);
}

.client-master-list__body {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;
}

.client-master-list__item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  text-align: left;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease;
  background: transparent;
  font-family: inherit;
  color: inherit;
}

.client-master-list__item:hover {
  background: rgba(10, 26, 47, 0.04);
}

.client-master-list__item--active {
  background: rgba(212, 175, 55, 0.1);
  box-shadow:
    inset 3px 0 0 var(--bank-gold),
    inset 0 0 0 1px rgba(212, 175, 55, 0.12);
}

.client-master-list__item--skeleton {
  padding: 0.5rem 1rem;
}

.client-master-list__item-avatar {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
  background: linear-gradient(135deg, rgba(10, 26, 47, 0.92), rgba(26, 34, 56, 0.92));
  color: #fff;
  border: 1px solid rgba(212, 175, 55, 0.35);
}

.client-master-list__item-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.client-master-list__item-code {
  font-weight: var(--font-weight-semibold);
  color: rgba(10, 26, 47, 0.92);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.client-master-list__item-email {
  font-size: var(--text-xs);
  color: var(--bank-gray-500);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.client-master-list__item-chevron {
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
  color: rgba(212, 175, 55, 0.95);
}

.client-master-list__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  font-size: var(--text-sm);
  color: var(--bank-danger);
  text-align: center;
  margin: 0.5rem 1rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(197, 48, 48, 0.18);
  background: rgba(197, 48, 48, 0.06);
}

.client-master-list__error p {
  margin: 0;
  max-width: 20rem;
  line-height: 1.45;
}

.client-master-list__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  font-size: var(--text-sm);
  color: var(--bank-gray-500);
  text-align: center;
}

.client-master-list__error-icon {
  width: 2.5rem;
  height: 2.5rem;
  color: var(--bank-danger);
  opacity: 0.95;
}

.client-master-list__empty-icon {
  width: 3rem;
  height: 3rem;
  color: rgba(212, 175, 55, 0.35);
}
</style>
